import { useState } from "react";
import { Box, Button, Typography, useMediaQuery } from "@mui/material";
import { CheckCircleRounded, WarningAmberRounded } from "@mui/icons-material";
import CoordinatorAvatars from "../_components/CoordinatorAvatars";
import ReadinessBar from "./ReadinessBar";
import {
  clientLabel,
  dateRangeLabel,
  NEON,
  placeLabel,
  SEVERITY_COLORS,
  THEME,
  whenLabel,
} from "./boardStyles";
import type { BoardShow } from "./boardTypes";

/** Cards shown before "Show all": two rows on desktop, three cards on a phone. */
const COLLAPSED_COUNT = 6;
const COLLAPSED_COUNT_PHONE = 3;

type Props = {
  shows: BoardShow[];
  loading: boolean;
  /** Only the shows the viewer coordinates (acting as coordinator). */
  mine?: boolean;
  /** Shows the viewer highlighted: neon border. */
  highlightIds?: Set<string>;
  onOpen: (show: BoardShow) => void;
};

/**
 * "Needs attention": shows running now or starting within 30 days that are not
 * fully booked or have client onboarding pending, most urgent first. The list and its order come from the API.
 */
export default function AttentionStrip({ shows, loading, mine = false, highlightIds, onOpen }: Props) {
  const [expanded, setExpanded] = useState(false);
  const phone = useMediaQuery("(max-width: 600px)");
  const collapsedCount = phone ? COLLAPSED_COUNT_PHONE : COLLAPSED_COUNT;
  const visible = expanded ? shows : shows.slice(0, collapsedCount);
  const critical = shows.filter((s) => s.attention?.severity === "critical").length;

  return (
    <Box component="section" aria-label="Shows that need attention">
      <Box sx={{ display: "flex", alignItems: "baseline", gap: "14px", mb: "14px", flexWrap: "wrap" }}>
        <Typography variant="h4" component="h2">
          Needs attention
        </Typography>
        <Typography sx={{ fontSize: "15px", opacity: 0.8 }}>
          {shows.length
            ? `${shows.length} ${mine ? "of your " : ""}show${shows.length === 1 ? "" : "s"} in the next 30 days need${shows.length === 1 ? "s" : ""} booking or client onboarding follow-up${critical ? ` · ${critical} within a week` : ""}`
            : mine
              ? "Your shows · next 30 days"
              : "Next 30 days"}
        </Typography>
      </Box>

      {!shows.length && !loading ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            padding: "16px 20px",
            borderRadius: "12px",
            background: THEME.black,
            border: `1px solid ${THEME.green}`,
          }}
        >
          <CheckCircleRounded sx={{ color: THEME.green }} />
          <Typography>
            {mine
              ? "No booking or client onboarding issues for your shows in the next 30 days."
              : "No booking or client onboarding issues for shows in the next 30 days."}
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: "16px",
          }}
        >
          {visible.map((show) => (
            <AttentionCard key={show._id} show={show} highlighted={!!highlightIds?.has(show._id)} onOpen={onOpen} />
          ))}
        </Box>
      )}

      {shows.length > collapsedCount ? (
        <Button
          size="small"
          onClick={() => setExpanded((v) => !v)}
          sx={{ mt: "14px", minWidth: "auto", px: 2, py: "4px" }}
        >
          {expanded ? "Show less" : `Show all ${shows.length}`}
        </Button>
      ) : null}
    </Box>
  );
}

function AttentionCard({
  show,
  highlighted,
  onOpen,
}: {
  show: BoardShow;
  highlighted: boolean;
  onOpen: (s: BoardShow) => void;
}) {
  const severity = show.attention?.severity ?? "info";
  const color = SEVERITY_COLORS[severity];
  const reasons = show.attention?.reasons ?? [];
  const subtitle = [clientLabel(show), placeLabel(show)].filter(Boolean).join(" · ");

  return (
    <Box
      component="button"
      type="button"
      onClick={() => onOpen(show)}
      aria-label={`${show.eventName}: ${whenLabel(show)}, ${show.readiness.percent}% ready`}
      sx={{
        all: "unset",
        boxSizing: "border-box",
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        minWidth: 0,
        padding: "14px 16px",
        borderRadius: "12px",
        background: THEME.black,
        border: `1px solid ${highlighted ? NEON.color : THEME.pinkSoft}`,
        borderLeft: `6px solid ${color}`,
        boxShadow: highlighted ? NEON.glow : "none",
        transition: "box-shadow 150ms ease, border-color 150ms ease",
        "&:hover": {
          boxShadow: highlighted ? NEON.glow : THEME.glow,
          borderColor: highlighted ? NEON.color : THEME.pink,
          borderLeftColor: color,
        },
        "&:focus-visible": { outline: `2px solid ${THEME.pink}`, outlineOffset: "2px" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px", minWidth: 0 }}>
          <WarningAmberRounded sx={{ fontSize: 20, color }} />
          <Typography sx={{ fontSize: "15px", fontWeight: 600, color }}>{whenLabel(show)}</Typography>
          <Typography sx={{ fontSize: "14px", opacity: 0.7, whiteSpace: "nowrap" }}>
            · {dateRangeLabel(show)}
          </Typography>
        </Box>
        <CoordinatorAvatars coordinators={show.coordinators} slots={3} size={26} disableTooltips />
      </Box>

      {show.clientOnboarding?.pending && <Typography sx={{ fontSize: "13px", fontWeight: 700, color: show.clientOnboarding.urgent ? "#FD355A" : "#FACE70" }}>Client onboarding incomplete</Typography>}

      <Box sx={{ minWidth: 0 }}>
        <Typography
          title={show.eventName}
          sx={{ fontSize: "18px", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
        >
          {show.eventName}
        </Typography>
        {subtitle ? (
          <Typography
            sx={{ fontSize: "14px", opacity: 0.7, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          >
            {subtitle}
          </Typography>
        ) : null}
      </Box>

      <Box component="ul" sx={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexWrap: "wrap", gap: "6px" }}>
        {reasons.map((r) => (
          <Box
            component="li"
            key={r}
            sx={{
              fontSize: "13px",
              lineHeight: "20px",
              padding: "1px 8px",
              borderRadius: "6px",
              color: THEME.white,
              border: `1px solid ${color}`,
            }}
          >
            {r}
          </Box>
        ))}
      </Box>

      <ReadinessBar show={show} />
    </Box>
  );
}
